import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import SelectAccount from 'screens/SelectAccount';
import AuthService from 'services/api/Auth';
import Storage from 'utils/Storage';
import handlePromise from 'utils/Promise';
import {
  clearSsoSessionKeys, getOrCreateSsoDeviceId, setRefreshToken,
} from 'utils/SsoSession';
import {
  extractSsoMessageCode, shouldResetSsoFlowByCode, translateSsoErrorCode,
} from 'utils/SsoError';
import ProfileService from 'services/api/Profile';
import InvestmentAccountService from 'services/api/InvestmentAccount';
import { User } from 'types/User';
import { Account } from 'types/Account';
import { LoadingContext } from 'context/Loading';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ACCOUNTS } from 'constants/routes';
import {
  updateUserAccount, updateUserEmail, updateUserId, updateUserRole, updateUserToken,
} from 'store/User';
import { loadAndLogAppInfo } from 'utils/appInfo';
import LoginSsoView, { STEPS } from './components/View';

function LoginSso() {
  const dispatch = useDispatch();
  const { toggleLoading } = useContext( LoadingContext );
  const [showAccountModal, setAccountModal] = useState( false );
  const [profile, setProfile] = useState<User>();
  const [accounts, setAccounts] = useState<Account[]>( [] );
  const [step, setStep] = useState<STEPS>( STEPS.REQUEST_OTP );
  const [identifier, setIdentifier] = useState( '' );
  const [submitError, setSubmitError] = useState<string | undefined>();
  const navigate = useNavigate();

  const selectAccount = useCallback( ( accountId: string ) => {
    dispatch( updateUserAccount( accountId ) );
  }, [] );

  const getUser = useCallback( async () => {
    toggleLoading( true );
    const [, profileResponse, profileData] = await handlePromise( ProfileService.getProfile() );
    if ( profileResponse.ok ) setProfile( profileData );

    if ( profileData?.email ) dispatch( updateUserEmail( profileData?.email ) );

    if ( profileData?.isNew ) {
      navigate( '/force-change-password' );
      toggleLoading( false );
      return;
    }

    const [, accountsResponse, accountsData] = await handlePromise(
      InvestmentAccountService.getInvestmentAccounts(),
    );
    if ( accountsResponse.ok ) setAccounts( accountsData );

    dispatch( updateUserRole( profileData?.role ) );
    dispatch( updateUserId( profileData?.userId ) );

    if ( !accountsData?.length && profileData?.role === 'Admin' ) {
      selectAccount( 'admin' );
      navigate( ADMIN_ACCOUNTS );
    } else if ( accountsData?.length === 1 && profileData?.role !== 'Admin' ) {
      selectAccount( accountsData[0].cuentaDeInversionId );
    } else if ( ( profileData?.role === 'Admin' && !!accountsData?.length ) || ( accountsData?.length > 1 ) ) {
      setAccountModal( true );
    }
    toggleLoading( false );
  }, [] );

  useEffect( () => {
    Storage.clear( 'grupoimpar:token' );
    loadAndLogAppInfo();
  }, [] );

  const resetSsoFlow = useCallback( () => {
    Storage.clear( 'grupoimpar:token' );
    clearSsoSessionKeys();
    setIdentifier( '' );
    setStep( STEPS.REQUEST_OTP );
  }, [] );

  const requestOtp = useCallback( async ( values: Record<string, any> ) => {
    toggleLoading( true );
    setSubmitError( undefined );
    const cleanIdentifier = String( values.identifier ?? '' ).trim().toLowerCase();
    const [error, response] = await handlePromise( AuthService.ssoLogin( {
      identifier: cleanIdentifier,
      deviceId: getOrCreateSsoDeviceId(),
      lang: 'es',
    } ) );
    toggleLoading( false );

    if ( !response.ok ) {
      const messageCode = extractSsoMessageCode( error );

      if ( String( error ).includes( '503' ) ) {
        setSubmitError( translateSsoErrorCode( 'SERVICE_UNAVAILABLE' ) );
        return;
      }

      if ( shouldResetSsoFlowByCode( messageCode ) ) resetSsoFlow();
      setSubmitError( translateSsoErrorCode( messageCode, 'REQUEST_OTP_FALLBACK' ) );
      return;
    }

    setIdentifier( cleanIdentifier );
    setStep( STEPS.VERIFY_OTP );
  }, [resetSsoFlow] );

  const verifyOtp = useCallback( async ( values: Record<string, any> ) => {
    toggleLoading( true );
    setSubmitError( undefined );
    const [error, response, responseData] = await handlePromise( AuthService.ssoConfirm( {
      identifier,
      deviceId: getOrCreateSsoDeviceId(),
      otpCode: String( values.otpCode ?? '' ).trim().toUpperCase(),
    } ) );
    toggleLoading( false );

    if ( !response.ok ) {
      const messageCode = extractSsoMessageCode( error );

      if ( shouldResetSsoFlowByCode( messageCode ) ) resetSsoFlow();

      setSubmitError( translateSsoErrorCode( messageCode, 'VERIFY_OTP_FALLBACK' ) );
      return;
    }

    const accessToken = responseData?.accessToken;
    if ( !accessToken ) {
      setSubmitError( translateSsoErrorCode( 'MISSING_ACCESS_TOKEN' ) );
      return;
    }

    Storage.set( 'grupoimpar:token', accessToken );
    setRefreshToken( responseData?.refreshToken );
    dispatch( updateUserToken( accessToken ) );
    getUser();
  }, [identifier, resetSsoFlow] );

  return (
    <>
      <LoginSsoView
        step={step}
        identifier={identifier}
        submitError={submitError}
        requestOtp={requestOtp}
        verifyOtp={verifyOtp}
        onChangeEmail={() => {
          setSubmitError( undefined );
          setIdentifier( '' );
          setStep( STEPS.REQUEST_OTP );
        }}
      />

      <SelectAccount
        show={showAccountModal}
        userRole={profile?.role}
        accounts={accounts}
        selectAccount={selectAccount}
      />
    </>
  );
}

export default LoginSso;
